import { useEstimatedTradeCostComparisonWidgetConfigSlice } from './store/configslice';
import { useAppSelector, useDispatch } from 'store/hooks';
import {
    clearData,
    selectEstimatedTradeCostWidgetsData,
} from 'widgets/estimatedTradeCost/store/data/dataSlice';
import { useCallback, useMemo } from 'react';
import { useEstimatedTradeCostComparisonWidgetComponentSlice } from './store/componentSlice';
import { toUid } from 'widgets/estimatedTradeCost/store/data/EstimatedTradeCostRequest';
import { usePortfolioSelectedOrder } from 'portfolio/store/hooks';
import { toRequest } from 'widgets/estimatedTradeCost/store/data/mapping';
import {
    useCheckWidgetStream,
    useClearResponses,
} from 'widgets/streaming/hooks';
import { EstimatedTradeCostComparisonData } from 'widgets/estimatedTradeCost/widget-comparison/estimatedTradeCostComparisonData';

export function useEstimatedTradeCostDataComparisonRequestUids() {
    const selectedOrder = usePortfolioSelectedOrder();
    const { config } = useEstimatedTradeCostComparisonWidgetConfigSlice();
    const { component } = useEstimatedTradeCostComparisonWidgetComponentSlice();

    return useMemo(() => {
        const requests = config.optimizations
            .map((o) => toRequest(selectedOrder, o, component))
            .filter((r) => !!r);
        return requests.map((r) => toUid(r!));
    }, [config, component, selectedOrder]);
}

export const useEstimatedTradeCostComparisonData: () => EstimatedTradeCostComparisonData =
    () => {
        const selectedOrder = usePortfolioSelectedOrder();
        const { config } = useEstimatedTradeCostComparisonWidgetConfigSlice();
        const { component } =
            useEstimatedTradeCostComparisonWidgetComponentSlice();
        const data = useAppSelector((s) =>
            selectEstimatedTradeCostWidgetsData(s)
        );

        return useMemo(() => {
            const requests = config.optimizations.map((optimizationConfig) => {
                const request = toRequest(
                    selectedOrder,
                    optimizationConfig,
                    component
                );
                const uid = request ? toUid(request) : 'No Request';
                const optimizationData = data[uid];

                return {
                    uid,
                    config: optimizationConfig,
                    data: optimizationData,
                };
            });

            const ret: EstimatedTradeCostComparisonData = {
                firstRequest:
                    requests && requests.length > 0
                        ? requests[0].data?.request
                        : undefined,
                requests,
                allCompleted: !requests.find(
                    (r) => r.data?.status !== 'Completed'
                ),
                haveFailure: !!requests.find(
                    (r) => r.data?.status === 'Failed'
                ),
                specifiedQty: requests[0]?.data?.request.specifiedQty,
            };

            return ret;
        }, [config, component, data, selectedOrder]);
    };

export function useReRunEstimatedTradeCostComparisonRequests() {
    const uids = useEstimatedTradeCostDataComparisonRequestUids();
    const clearRestResponses = useClearResponses(uids);
    const dispatcher = useDispatch();
    const checkWidgetStream = useCheckWidgetStream();

    return useCallback(() => {
        clearRestResponses();
        for (const uid of uids) {
            if (uid) {
                dispatcher(clearData(uid));
            }
        }
        setTimeout(() => {
            checkWidgetStream();
        });
    }, [clearRestResponses, checkWidgetStream, dispatcher, uids]);
}
